/**
 * RECENTLY VIEWED
 *
 * Tiny localStorage-backed store of the last N items a user visited,
 * grouped by entity type. Zero-backend — purely client-side so we can
 * ship the dashboard "Recently viewed" row without waiting on the API.
 *
 * Usage:
 *   // In a page that shows a single entity (job, freelancer, gig):
 *   useTrackRecentlyViewed('job', { id, title, ...summary });
 *
 *   // In a dashboard row:
 *   const jobs = useRecentlyViewed('job');
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'cbp.recently-viewed.v1';
const MAX_PER_TYPE = 12;

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore quota errors
  }
};

// Minimal store + pub/sub so useSyncExternalStore can react to changes
// made from other React subtrees within the same tab.
const listeners = new Set();
let cache = readStore();

const emit = () => {
  cache = readStore();
  listeners.forEach((l) => l());
};

// Keep tabs in sync.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) emit();
  });
}

const subscribe = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

const getSnapshot = () => cache;

/**
 * Track a single entity view. Safe to call on every render — duplicate
 * entries for the same id are deduped and moved to the head.
 */
export const useTrackRecentlyViewed = (type, item) => {
  useEffect(() => {
    if (!type || !item || item.id == null) return;
    const store = readStore();
    const bucket = Array.isArray(store[type]) ? store[type] : [];
    const next = [
      { ...item, id: String(item.id), viewed_at: new Date().toISOString() },
      ...bucket.filter((b) => String(b.id) !== String(item.id)),
    ].slice(0, MAX_PER_TYPE);
    writeStore({ ...store, [type]: next });
    emit();
    // We intentionally depend on the id + title so a re-render with
    // the same entity doesn't spam localStorage.
  }, [type, item?.id, item?.title, item?.name]);
};

/**
 * Read a bucket of recently viewed items for a given type.
 */
export const useRecentlyViewed = (type) => {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return useMemo(() => {
    const bucket = store?.[type];
    return Array.isArray(bucket) ? bucket : [];
  }, [store, type]);
};

/**
 * Clear one or all buckets.
 */
export const clearRecentlyViewed = (type) => {
  if (!type) {
    writeStore({});
  } else {
    const store = readStore();
    delete store[type];
    writeStore(store);
  }
  emit();
};
